/**
 * Standard responsive breakpoints for Novala.
 *
 * All pages, components, and utilities import from this file. Never
 * hardcode breakpoint values inline. If a breakpoint needs tweaking,
 * change it here and everything updates together.
 *
 * Tiers:
 *   mobile:  width <  640
 *   tablet:  640 <= width <  1024
 *   desktop: 1024 <= width <  1440
 *   wide:    width >= 1440
 *
 * Values are the LOWER bound of each tier. The named value marks the
 * transition INTO that tier (i.e. BREAKPOINTS.tablet = 640 means
 * tablet layout starts at 640px).
 *
 * Chosen to align with industry defaults (Tailwind sm/lg/2xl, Material
 * Design, Bootstrap sm/lg/xl) so future contributors recognize them.
 */

export const BREAKPOINTS = {
  /** Mobile to tablet transition: 640px */
  tablet: 640,
  /** Tablet to desktop transition: 1024px */
  desktop: 1024,
  /** Desktop to wide transition: 1440px */
  wide: 1440,
} as const;

/**
 * CSS media query strings using the breakpoints above.
 *
 * Use in inline styles or template literals when writing media queries
 * by hand (e.g. inside a <style> tag).
 *
 * Example:
 *   `padding: 16px;
 *    ${MEDIA.tablet} { padding: 24px; }
 *    ${MEDIA.desktop} { padding: 32px; }`
 */
export const MEDIA = {
  tablet: `@media (min-width: ${BREAKPOINTS.tablet}px)`,
  desktop: `@media (min-width: ${BREAKPOINTS.desktop}px)`,
  wide: `@media (min-width: ${BREAKPOINTS.wide}px)`,
} as const;

/**
 * Programmatic matchMedia query strings (no @media prefix) for use
 * with window.matchMedia() or React responsive libraries that take
 * a bare query string.
 */
export const MEDIA_QUERY = {
  mobile: `(max-width: ${BREAKPOINTS.tablet - 1}px)`,
  tablet: `(min-width: ${BREAKPOINTS.tablet}px) and (max-width: ${BREAKPOINTS.desktop - 1}px)`,
  desktop: `(min-width: ${BREAKPOINTS.desktop}px) and (max-width: ${BREAKPOINTS.wide - 1}px)`,
  wide: `(min-width: ${BREAKPOINTS.wide}px)`,
} as const;